import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Plus,
  Undo2,
  Redo2,
  Filter,
  SortAsc,
  Upload,
  Settings2,
  Trash2,
  Search,
  MoreHorizontal,
  Share2,
  History,
  Printer,
  Sparkles,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataGrid, DataGridTask, DataGridColumn } from '@/components/grid/DataGrid';
import { ViewContainer } from '@/components/views/ViewContainer';
import { useTasksQuery, useCreateTask, useBatchDeleteTasks, useMoveTask } from '@/hooks/useTasks';
import { useColumnsQuery, useCreateColumn, useUpdateColumn } from '@/hooks/useColumns';
import { useSheetDependenciesQuery } from '@/hooks/useDependencies';
import { useOptimisticUpdates } from '@/hooks/useOptimisticUpdates';
import { useGridStore } from '@/stores/gridStore';
import { useGridKeyboard } from '@/hooks/useGridKeyboard';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { useCollaboration } from '@/hooks/useCollaboration';
import { ImportDialog } from '@/components/import-export/ImportDialog';
import { ExportDropdown } from '@/components/import-export/ExportDropdown';
import { PresenceAvatars } from '@/components/collaboration/PresenceAvatars';
import { AiQueryBar } from '@/components/ai/AiQueryBar';
import { TaskPanel } from '@/components/task/TaskPanel';
import { ActivityFeed } from '@/components/activity/ActivityFeed';
import { ColumnType } from '@projectflow/shared';

type FilterOperator =
  | 'equals'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'between'
  | 'isEmpty'
  | 'isNotEmpty';

const FILTER_OPERATORS: Array<{ value: FilterOperator; label: string }> = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal' },
  { value: 'between', label: 'Between' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
];

const OPERATORS_WITHOUT_VALUE = new Set<FilterOperator>(['isEmpty', 'isNotEmpty']);

function toComparableNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const asDate = Date.parse(value);
    if (!Number.isNaN(asDate)) return asDate;
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) return asNumber;
  }
  return null;
}

function compareValues(a: unknown, b: unknown): number {
  const aNum = toComparableNumber(a);
  const bNum = toComparableNumber(b);

  if (aNum !== null && bNum !== null) {
    return aNum - bNum;
  }

  const aString = String(a ?? '').toLowerCase();
  const bString = String(b ?? '').toLowerCase();
  if (aString < bString) return -1;
  if (aString > bString) return 1;
  return 0;
}

function isCellEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function SheetView() {
  const { projectId, sheetId } = useParams<{ projectId: string; sheetId: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [aiQueryOpen, setAiQueryOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<{
    columnId: string;
    operator: FilterOperator;
    value: string;
    value2: string;
  }>({
    columnId: '',
    operator: 'contains',
    value: '',
    value2: '',
  });
  const [sortDraft, setSortDraft] = useState<{ columnId: string; direction: 'asc' | 'desc' }>({
    columnId: '',
    direction: 'asc',
  });

  // Cmd+K / Ctrl+K keyboard shortcut for AI query
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setAiQueryOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Real-time collaboration
  useRealtimeSync();
  const { users } = useCollaboration(projectId);

  // Store state
  const {
    selectedRows,
    canUndo,
    canRedo,
    setActiveSheet,
    clearSelection,
    selectAllRows,
    filters,
    sortConfig,
    addFilter,
    removeFilter,
    addSort,
    removeSort,
    clearFilters,
    clearSort,
  } = useGridStore();

  // Queries
  const {
    data: tasks = [],
    isLoading: tasksLoading,
    error: tasksError
  } = useTasksQuery(sheetId || '', { search: searchQuery || undefined });

  const {
    data: columns = [],
    isLoading: columnsLoading
  } = useColumnsQuery(sheetId || '');

  const {
    data: dependencies = []
  } = useSheetDependenciesQuery(sheetId || '');

  // Mutations
  const createTaskMutation = useCreateTask(sheetId || '');
  const batchDeleteMutation = useBatchDeleteTasks(sheetId || '');
  const moveTaskMutation = useMoveTask(sheetId || '');
  const createColumnMutation = useCreateColumn(sheetId || '');
  const updateColumnMutation = useUpdateColumn(sheetId || '');
  const {
    undo,
    redo,
    updateCell,
  } = useOptimisticUpdates(sheetId || '');

  // Set active sheet on mount
  useEffect(() => {
    if (sheetId) {
      setActiveSheet(sheetId);
    }
    return () => {
      setActiveSheet(null);
    };
  }, [sheetId, setActiveSheet]);

  // Keyboard shortcuts
  useGridKeyboard({
    onDelete: useCallback(() => {
      const selectedIds = Array.from(selectedRows);
      if (selectedIds.length > 0) {
        batchDeleteMutation.mutate(selectedIds);
      }
    }, [selectedRows, batchDeleteMutation]),
    onUndo: undo,
    onRedo: redo,
    onSelectAll: useCallback(() => {
      const taskIds = tasks.map((t: { id: string }) => t.id);
      selectAllRows(taskIds);
    }, [tasks, selectAllRows]),
    onEscape: clearSelection,
  });

  useEffect(() => {
    if (columns.length === 0) return;

    if (!filterDraft.columnId) {
      setFilterDraft((prev) => ({ ...prev, columnId: columns[0].id }));
    }

    if (!sortDraft.columnId) {
      setSortDraft((prev) => ({ ...prev, columnId: columns[0].id }));
    }
  }, [columns, filterDraft.columnId, sortDraft.columnId]);

  const columnMap = useMemo(() => {
    return new Map(columns.map((column) => [column.id, column]));
  }, [columns]);

  const getTaskColumnValue = useCallback(
    (task: any, columnId: string) => {
      const column = columnMap.get(columnId);
      if (!column) return undefined;

      if (column.isPrimary) {
        return task.name;
      }

      const customFields = (task.customFields as Record<string, unknown> | undefined) ?? {};
      return customFields[columnId];
    },
    [columnMap]
  );

  const normalizeFilterInput = useCallback(
    (columnId: string, rawValue: string): unknown => {
      const column = columnMap.get(columnId);
      if (!column) return rawValue;

      const trimmed = rawValue.trim();
      if (trimmed.length === 0) return '';

      switch (column.columnType) {
        case ColumnType.NUMBER:
        case ColumnType.CURRENCY:
        case ColumnType.PERCENT:
        case ColumnType.DURATION: {
          const numeric = Number(trimmed);
          return Number.isNaN(numeric) ? trimmed : numeric;
        }
        case ColumnType.CHECKBOX:
          return ['true', '1', 'yes', 'y'].includes(trimmed.toLowerCase());
        default:
          return trimmed;
      }
    },
    [columnMap]
  );

  // Event handlers
  const handleAddTask = useCallback(() => {
    createTaskMutation.mutate({ name: 'New Task' });
  }, [createTaskMutation]);

  const handleAddColumn = useCallback(() => {
    const nextPosition = columns.length + 1;
    createColumnMutation.mutate({
      name: `Column ${nextPosition}`,
      columnType: ColumnType.TEXT,
      position: nextPosition,
    });
  }, [createColumnMutation, columns.length]);

  const handleDeleteSelected = useCallback(() => {
    const selectedIds = Array.from(selectedRows);
    if (selectedIds.length > 0) {
      batchDeleteMutation.mutate(selectedIds);
      clearSelection();
    }
  }, [selectedRows, batchDeleteMutation, clearSelection]);

  const handleImportSuccess = useCallback((importedProjectId: string) => {
    navigate(`/projects/${importedProjectId}`);
  }, [navigate]);

  const handleShare = useCallback(() => {
    // TODO: Implement share functionality
    console.log('Share clicked');
  }, []);

  const handleColumnVisibilityChange = useCallback((columnId: string, visible: boolean) => {
    updateColumnMutation.mutate({ columnId, updates: { isHidden: !visible } });
  }, [updateColumnMutation]);

  // Filter visible tasks based on hierarchy (collapsed rows)
  const expandedRows = useGridStore((state) => state.expandedRows);
  const hierarchyVisibleTasks = useMemo(() => {
    const result: typeof tasks = [];
    const collapsedParents = new Set<string>();

    for (const task of tasks) {
      // Check if any parent is collapsed
      let isHidden = false;
      let parentId = task.parentId;

      while (parentId) {
        if (collapsedParents.has(parentId)) {
          isHidden = true;
          break;
        }
        const parent = tasks.find((t: { id: string; parentId: string | null }) => t.id === parentId);
        parentId = parent?.parentId || null;
      }

      if (!isHidden) {
        result.push(task);

        // Track collapsed summary tasks
        if (task.isSummary && !expandedRows.has(task.id)) {
          collapsedParents.add(task.id);
        }
      }
    }

    return result;
  }, [tasks, expandedRows]);

  const visibleTasks = useMemo(() => {
    let filtered = [...hierarchyVisibleTasks];

    if (filters.length > 0) {
      filtered = filtered.filter((task) => {
        return filters.every((filter) => {
          const cellValue = getTaskColumnValue(task, filter.columnId);

          if (filter.operator === 'isEmpty') {
            return isCellEmpty(cellValue);
          }
          if (filter.operator === 'isNotEmpty') {
            return !isCellEmpty(cellValue);
          }

          const stringValue = String(cellValue ?? '').toLowerCase();
          const filterValue = String(filter.value ?? '').toLowerCase();

          switch (filter.operator) {
            case 'equals':
              return compareValues(cellValue, filter.value) === 0;
            case 'contains':
              return stringValue.includes(filterValue);
            case 'startsWith':
              return stringValue.startsWith(filterValue);
            case 'endsWith':
              return stringValue.endsWith(filterValue);
            case 'gt':
              return compareValues(cellValue, filter.value) > 0;
            case 'gte':
              return compareValues(cellValue, filter.value) >= 0;
            case 'lt':
              return compareValues(cellValue, filter.value) < 0;
            case 'lte':
              return compareValues(cellValue, filter.value) <= 0;
            case 'between':
              if (filter.value2 === undefined) return false;
              return (
                compareValues(cellValue, filter.value) >= 0 &&
                compareValues(cellValue, filter.value2) <= 0
              );
            default:
              return true;
          }
        });
      });
    }

    if (sortConfig.length > 0) {
      filtered.sort((a, b) => {
        for (const sortRule of sortConfig) {
          const aValue = getTaskColumnValue(a, sortRule.columnId);
          const bValue = getTaskColumnValue(b, sortRule.columnId);
          const comparison = compareValues(aValue, bValue);
          if (comparison !== 0) {
            return sortRule.direction === 'asc' ? comparison : -comparison;
          }
        }
        return 0;
      });
    }

    return filtered;
  }, [hierarchyVisibleTasks, filters, sortConfig, getTaskColumnValue]);

  const selectedCount = selectedRows.size;
  const hasFilters = filters.length > 0;
  const hasSorts = sortConfig.length > 0;

  const handleAddFilter = useCallback(() => {
    if (!filterDraft.columnId) return;

    if (
      !OPERATORS_WITHOUT_VALUE.has(filterDraft.operator) &&
      !filterDraft.value.trim()
    ) {
      return;
    }

    addFilter({
      columnId: filterDraft.columnId,
      operator: filterDraft.operator,
      value: normalizeFilterInput(filterDraft.columnId, filterDraft.value),
      value2:
        filterDraft.operator === 'between'
          ? normalizeFilterInput(filterDraft.columnId, filterDraft.value2)
          : undefined,
    });

    setFilterDraft((prev) => ({ ...prev, value: '', value2: '' }));
  }, [filterDraft, addFilter, normalizeFilterInput]);

  const handleAddSort = useCallback(() => {
    if (!sortDraft.columnId) return;
    addSort(sortDraft.columnId, sortDraft.direction);
  }, [sortDraft, addSort]);

  const getColumnName = useCallback(
    (columnId: string) => {
      return columnMap.get(columnId)?.name || columnId;
    },
    [columnMap]
  );

  // Loading state
  if (tasksLoading || columnsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Error state
  if (tasksError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Error loading sheet</h2>
          <p className="mt-2 text-gray-600">
            {tasksError instanceof Error ? tasksError.message : 'Unknown error'}
          </p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* AI Query Bar */}
      <AiQueryBar
        projectId={projectId || ''}
        isOpen={aiQueryOpen}
        onClose={() => setAiQueryOpen(false)}
      />

      {/* Sheet Actions Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
        {/* Left section - Add task and quick actions */}
        <div className="flex items-center gap-2">
          {/* Add task button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={handleAddTask}
                  disabled={createTaskMutation.isPending}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Task
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add new task (Ctrl+N)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Separator orientation="vertical" className="h-6" />

          {/* Undo/Redo */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={undo}
                  disabled={!canUndo()}
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={redo}
                  disabled={!canRedo()}
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Redo (Ctrl+Y)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Center section - Search + AI */}
        <div className="flex-1 max-w-md mx-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  data-tour="ai-query"
                  onClick={() => setAiQueryOpen(true)}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span className="text-xs text-muted-foreground">⌘K</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>AI Assistant (⌘K)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-1">
          {/* Selection actions */}
          {selectedCount > 0 && (
            <>
              <Badge variant="secondary" className="mr-2">
                {selectedCount} selected
              </Badge>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDeleteSelected}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete selected (Del)</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Separator orientation="vertical" className="h-6 mx-1" />
            </>
          )}

          {/* Filter */}
          <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={hasFilters ? 'secondary' : 'ghost'}
                size="icon"
                className="relative"
              >
                <Filter className="h-4 w-4" />
                {hasFilters && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-600 text-[10px] text-white flex items-center justify-center">
                    {filters.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px]">
              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Filters</h4>
                  <p className="text-xs text-muted-foreground">Filter tasks by column values.</p>
                </div>

                <div className="grid gap-3">
                  <div className="space-y-2">
                    <Label>Column</Label>
                    <Select
                      value={filterDraft.columnId}
                      onValueChange={(value) => setFilterDraft((prev) => ({ ...prev, columnId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        {columns.map((column) => (
                          <SelectItem key={column.id} value={column.id}>
                            {column.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Operator</Label>
                    <Select
                      value={filterDraft.operator}
                      onValueChange={(value) =>
                        setFilterDraft((prev) => ({ ...prev, operator: value as FilterOperator }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {FILTER_OPERATORS.map((operator) => (
                          <SelectItem key={operator.value} value={operator.value}>
                            {operator.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {!OPERATORS_WITHOUT_VALUE.has(filterDraft.operator) && (
                    <div className="space-y-2">
                      <Label>Value</Label>
                      <Input
                        value={filterDraft.value}
                        onChange={(e) => setFilterDraft((prev) => ({ ...prev, value: e.target.value }))}
                        placeholder="Enter value"
                      />
                    </div>
                  )}

                  {filterDraft.operator === 'between' && (
                    <div className="space-y-2">
                      <Label>Second value</Label>
                      <Input
                        value={filterDraft.value2}
                        onChange={(e) => setFilterDraft((prev) => ({ ...prev, value2: e.target.value }))}
                        placeholder="Enter second value"
                      />
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={handleAddFilter} className="flex-1">
                      Apply filter
                    </Button>
                    <Button variant="outline" onClick={clearFilters} disabled={!hasFilters}>
                      Clear
                    </Button>
                  </div>
                </div>

                {filters.length > 0 && (
                  <div className="space-y-2 border-t pt-3">
                    {filters.map((filter) => (
                      <div key={filter.columnId} className="flex items-center justify-between rounded-md border px-2 py-1.5 text-xs">
                        <span className="truncate pr-2">
                          {getColumnName(filter.columnId)} {filter.operator}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeFilter(filter.columnId)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Sort */}
          <Popover open={sortPopoverOpen} onOpenChange={setSortPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={hasSorts ? 'secondary' : 'ghost'}
                size="icon"
                className="relative"
              >
                <SortAsc className="h-4 w-4" />
                {hasSorts && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-600 text-[10px] text-white flex items-center justify-center">
                    {sortConfig.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[320px]">
              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Sort</h4>
                  <p className="text-xs text-muted-foreground">Sort tasks by one or more columns.</p>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Column</Label>
                    <Select
                      value={sortDraft.columnId}
                      onValueChange={(value) => setSortDraft((prev) => ({ ...prev, columnId: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        {columns.map((column) => (
                          <SelectItem key={column.id} value={column.id}>
                            {column.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Direction</Label>
                    <Select
                      value={sortDraft.direction}
                      onValueChange={(value) =>
                        setSortDraft((prev) => ({ ...prev, direction: value as 'asc' | 'desc' }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Direction" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asc">Ascending</SelectItem>
                        <SelectItem value="desc">Descending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddSort} className="flex-1">
                      Apply sort
                    </Button>
                    <Button variant="outline" onClick={clearSort} disabled={!hasSorts}>
                      Clear
                    </Button>
                  </div>
                </div>

                {sortConfig.length > 0 && (
                  <div className="space-y-2 border-t pt-3">
                    {sortConfig.map((sortRule) => (
                      <div key={sortRule.columnId} className="flex items-center justify-between rounded-md border px-2 py-1.5 text-xs">
                        <span className="truncate pr-2">
                          {getColumnName(sortRule.columnId)} ({sortRule.direction})
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeSort(sortRule.columnId)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Import */}
          <ImportDialog
            organizationId=""
            workspaceId=""
            onSuccess={handleImportSuccess}
          >
            <Button variant="ghost" size="icon" data-tour="import-button" title="Import">
              <Upload className="h-4 w-4" />
            </Button>
          </ImportDialog>

          {/* Export */}
          <ExportDropdown projectId={projectId || ''} />

          <Separator orientation="vertical" className="h-6 mx-1" />

          {/* Presence avatars */}
          <div data-tour="presence-avatars">
            {users.length > 0 && <PresenceAvatars users={users} />}
          </div>

          {/* Activity feed toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activityOpen ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setActivityOpen(!activityOpen)}
                >
                  <History className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Activity</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Share */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleShare}>
                  <Share2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Share</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* More actions dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={clearFilters} disabled={!hasFilters}>
                <Filter className="h-4 w-4 mr-2" />
                Clear filters
              </DropdownMenuItem>
              <DropdownMenuItem onClick={clearSort} disabled={!hasSorts}>
                <SortAsc className="h-4 w-4 mr-2" />
                Clear sort
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.print()}>
                <Printer className="h-4 w-4 mr-2" />
                Print / PDF
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings2 className="h-4 w-4 mr-2" />
                Sheet settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Active filters/sorts indicator */}
      {(hasFilters || hasSorts) && (
        <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-50 border-b text-sm">
          {hasFilters && (
            <span className="text-blue-700">
              {filters.length} filter{filters.length > 1 ? 's' : ''} active
            </span>
          )}
          {hasFilters && hasSorts && (
            <span className="text-gray-400">|</span>
          )}
          {hasSorts && (
            <span className="text-blue-700">
              Sorted by {sortConfig.length} column{sortConfig.length > 1 ? 's' : ''}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs ml-auto"
            onClick={() => {
              clearFilters();
              clearSort();
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Main content area - ViewContainer handles view switching */}
      <div className="flex-1 overflow-hidden" data-tour="gantt-toggle">
        <ViewContainer
          data-tour="grid-view"
          sheetId={sheetId || ''}
          gridView={
            <DataGrid
              data={visibleTasks as unknown as DataGridTask[]}
              columns={columns as unknown as DataGridColumn[]}
              onCellChange={(taskId, columnId, value, oldValue) => {
                updateCell(taskId, columnId, value, oldValue);
              }}
              onTaskCreate={() => handleAddTask()}
              onColumnAdd={() => handleAddColumn()}
              onTaskReorder={(taskId, targetTaskId, position) => {
                if (position === 'before') {
                  // Find the task before the target to use as afterTaskId
                  const targetIndex = visibleTasks.findIndex((t: any) => t.id === targetTaskId);
                  const afterTaskId = targetIndex > 0 ? (visibleTasks[targetIndex - 1] as any).id : null;
                  moveTaskMutation.mutate({ taskId, move: { afterTaskId } });
                } else {
                  moveTaskMutation.mutate({ taskId, move: { afterTaskId: targetTaskId } });
                }
              }}
            />
          }
          columns={columns.map((col) => ({
            id: col.id,
            name: col.name,
            visible: !col.isHidden,
          }))}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onFilter={() => setFilterPopoverOpen(true)}
          onSort={() => setSortPopoverOpen(true)}
          onGroup={() => { /* Handled by dropdown in ViewToolbar */ }}
        />
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-1 border-t bg-gray-50 text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span>
            {visibleTasks.length}
            {visibleTasks.length !== tasks.length ? ` of ${tasks.length}` : ''} tasks
          </span>
          <span>{columns.length} columns</span>
          {dependencies.length > 0 && (
            <span>{dependencies.length} dependencies</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {selectedCount > 0 && (
            <span className="text-blue-600">{selectedCount} selected</span>
          )}
        </div>
      </div>

      {/* Task detail panel */}
      <TaskPanel sheetId={sheetId || ''} />

      {/* Activity feed sidebar */}
      {activityOpen && projectId && (
        <div className="fixed right-0 top-16 bottom-0 w-80 bg-white border-l border-gray-200 shadow-lg z-40">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Activity</h3>
            <button
              className="p-1 hover:bg-gray-100 rounded"
              onClick={() => setActivityOpen(false)}
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          <ActivityFeed projectId={projectId} />
        </div>
      )}
    </div>
  );
}

export default SheetView;
